import type { ChatMessage, LlmMessage, ToolCallState } from '../../../../widget/types';
import type { IterationUsageRow, HistorySummarizationEntry, IterationUsageEntry } from '../../../../widget/types/iteration-usage';
import { t } from '../../../../widget/i18n';

export type { IterationUsageRow };

type RawConversationMessage = {
  role: string;
  content: string | unknown[];
  tool_call_id?: string;
  turnId?: string | null;
};

/** Truncate to maxLen chars without cutting a word in half; matches the
 *  server's title truncation (webchat-conversation.service.ts) so the
 *  optimistic sidebar title doesn't change once the conversation persists. */
export function truncateTitle(text: string, maxLen = 60): string {
  const trimmed = text.trim();
  if (trimmed.length <= maxLen) return trimmed;
  const cut = trimmed.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  const boundary = lastSpace > maxLen * 0.5 ? cut.slice(0, lastSpace) : cut;
  return `${boundary.trim()}…`;
}

function extractText(content: string | unknown[]): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .filter((b: unknown) => (b as { type: string }).type === 'text')
      .map((b: unknown) => (b as { text: string }).text)
      .join('');
  }
  return '';
}

/** Mirrors the server's chat-orchestrator isToolResultSuccessful() so history
 *  playback shows the same success/failure state the live turn saw. */
function isToolResultSuccessful(content: unknown): boolean {
  if (typeof content !== 'string') return true;
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed)) {
      return !parsed.some((item: any) => item?.isError === true);
    }
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      return false;
    }
  } catch {
    // Not JSON - treat as success
  }
  return true;
}

/** Map backend conversation messages to widget ChatMessage format.
 *  Consecutive assistant messages from a single multi-step turn are merged into one bubble.
 *  Also extracts tool_use blocks so inputs are available after reload. */
export function mapMessages(
  raw: RawConversationMessage[],
  baseTime: number,
  iterationUsage?: IterationUsageRow[],
): { messages: ChatMessage[]; toolCalls: ToolCallState[]; historyMessages: LlmMessage[] } {
  const filtered = raw.filter(m => m.role === 'user' || m.role === 'assistant');

  const usageByTurnId = new Map<string, IterationUsageEntry[]>();
  const summarizationByTurnId = new Map<string, HistorySummarizationEntry[]>();
  if (iterationUsage?.length) {
    for (const row of iterationUsage) {
      if (row.kind === 'summarization') {
        const bucket = summarizationByTurnId.get(row.turnId) ?? [];
        bucket.push({ turnId: row.turnId, llmCallCount: row.llmCallCount, totalTokens: row.totalTokens });
        summarizationByTurnId.set(row.turnId, bucket);
        continue;
      }
      const bucket = usageByTurnId.get(row.turnId) ?? [];
      bucket.push({
        iteration: row.iteration,
        totalTokens: row.totalTokens,
        tools: row.tools ?? [],
        llmRequestPayload: row.llmRequestPayload ?? null,
        cacheReadTokens: row.cacheReadTokens ?? 0,
        createdAt: row.createdAt ?? null,
        turnId: row.turnId,
        source: row.source ?? 'main',
        parentIteration: row.parentIteration ?? null,
        analystCallId: row.analystCallId ?? null,
        resultName: row.resultName ?? null,
        llmResponseText: row.llmResponseText ?? null,
      });
      usageByTurnId.set(row.turnId, bucket);
    }
    for (const bucket of usageByTurnId.values()) {
      bucket.sort((a, b) => a.iteration - b.iteration);
    }
  }

  // Canonical structured history for resending to the LLM - the raw persisted
  // messages already have the right shape (they're what the server itself saved),
  // minus the system-prompt entry, which the backend prepends fresh on every request.
  const historyMessages: LlmMessage[] = raw
    .filter(m => m.role === 'user' || m.role === 'assistant' || m.role === 'tool')
    .map(m => ({
      role: m.role as LlmMessage['role'],
      content: m.content as LlmMessage['content'],
      ...(m.tool_call_id ? { tool_call_id: m.tool_call_id } : {}),
    }));

  // Merge consecutive assistant messages; track which merged index each raw assistant belongs to.
  const merged: Array<{ role: string; content: string; turnIds: string[] }> = [];
  const rawAssistantToMergedIndex: number[] = [];

  for (const msg of filtered) {
    const text = extractText(msg.content);
    const msgTurnId = msg.role === 'assistant' ? msg.turnId ?? null : null;
    const last = merged[merged.length - 1];
    if (msg.role === 'assistant' && last?.role === 'assistant') {
      if (text) last.content = last.content ? `${last.content}\n\n${text}` : text;
      if (msgTurnId && !last.turnIds.includes(msgTurnId)) last.turnIds.push(msgTurnId);
      rawAssistantToMergedIndex.push(merged.length - 1);
    } else {
      merged.push({ role: msg.role, content: text, turnIds: msgTurnId ? [msgTurnId] : [] });
      if (msg.role === 'assistant') rawAssistantToMergedIndex.push(merged.length - 1);
    }
  }

  // A merged bubble can span several turns (each turn restarts `iteration` at 1), so gather the
  // runs of every turn and order them by when they happened.
  const runsForTurns = (turnIds: string[]) => {
    const runs = turnIds.flatMap((id) => usageByTurnId.get(id) ?? []);
    if (runs.length > 1 && runs.every((r) => r.createdAt)) {
      runs.sort((a, b) => a.createdAt!.localeCompare(b.createdAt!) || a.iteration - b.iteration);
    }
    return runs;
  };

  const now = Date.now().toString(36);
  const messages: ChatMessage[] = merged.map((m, i) => {
    const runs = runsForTurns(m.turnIds);
    const summaries = m.turnIds.flatMap((id) => summarizationByTurnId.get(id) ?? []);
    return {
      id: `loaded-${i}-${now}`,
      role: m.role as 'user' | 'assistant',
      content: m.content,
      timestamp: baseTime + i * 1000,
      ...(runs.length ? { iterationTokens: runs } : {}),
      ...(summaries.length ? { summarizationEvents: summaries } : {}),
    };
  });

  // tool_call_id -> raw result content, so we can tell success from failure
  // the same way the server does (isToolResultSuccessful).
  const resultContentById = new Map<string, unknown>();
  for (const msg of raw) {
    if (msg.role === 'tool' && msg.tool_call_id) {
      resultContentById.set(msg.tool_call_id, msg.content);
    }
  }

  // Extract tool_use blocks from raw assistant messages
  const toolCalls: ToolCallState[] = [];
  let assistantIdx = 0;
  for (const msg of filtered) {
    if (msg.role !== 'assistant') continue;
    const mergedIdx = rawAssistantToMergedIndex[assistantIdx++];
    const messageId = messages[mergedIdx]?.id;
    if (!messageId || !Array.isArray(msg.content)) continue;
    for (const block of msg.content as Array<Record<string, unknown>>) {
      if (block.type !== 'tool_use') continue;
      const id = (block.id as string) || '';
      const name = (block.name as string) || '';
      const inputs = block.input as Record<string, unknown> | undefined;
      if (!id || !name) continue;

      const resultContent = resultContentById.get(id);
      const success = isToolResultSuccessful(resultContent);
      const resultText = typeof resultContent === 'string' ? resultContent : undefined;
      toolCalls.push({
        id,
        name,
        status: success ? 'done' : 'error',
        inputs,
        messageId,
        ...(success ? { result: resultText } : { error: resultText }),
      });
    }
  }

  return { messages, toolCalls, historyMessages };
}

/** A plain, theme-matched assistant bubble representing a failed/timed-out turn.
 *  `turnId` present -> live/reconstructed-from-memory, offers Retry/Dismiss.
 *  `turnId` absent -> reconstructed from persisted history after reload, no
 *  live turn left to retry - just an explanation. */
export function buildErrorBubble(status: 'error' | 'timeout', turnId?: string): ChatMessage {
  return {
    id: `turn-error-${turnId ?? 'reload'}-${Date.now().toString(36)}`,
    role: 'assistant',
    content: status === 'timeout'
      ? (t('chatTimeout') || 'The request took too long. Please try again.')
      : (t('chatError') || 'Sorry, an error occurred. Please try again.'),
    timestamp: Date.now(),
    turnError: { turnId },
  };
}

/** After loading a persisted conversation, append an explanatory error bubble if
 *  it ended in a backend error with no trailing assistant reply to show for it -
 *  otherwise reopening it after a reload looks like a question nobody answered. */
export function withPersistedErrorBubble(
  messages: ChatMessage[],
  conv: { status: string; errorMessage: string | null },
): ChatMessage[] {
  const last = messages[messages.length - 1];
  if (conv.status !== 'error' || last?.role === 'assistant') return messages;
  return [...messages, buildErrorBubble('error')];
}
